import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import SendIcon from '@mui/icons-material/Send'
import LinkRoundedIcon from '@mui/icons-material/LinkRounded'
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import ActionsAPI from 'services/ActionsAPI'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import { groupConsecutive } from 'utils/activityGrouping'

const KIND_META = {
  created:   { label: 'created this remedial',   colour: '#0f766e', icon: AddCircleOutlineIcon },
  updated:   { label: 'updated the remedial',   colour: '#33becc', icon: EditOutlinedIcon },
  converted: { label: 'converted it to a task', colour: '#15803d', icon: CheckCircleOutlineIcon },
  dismissed: { label: 'dismissed the remedial', colour: '#6b7280', icon: RemoveCircleOutlineIcon },
  reopened:  { label: 'reopened the remedial',  colour: '#c2410c', icon: RestartAltIcon },
  commented: { label: 'left a comment',        colour: '#5b21b6', icon: ChatBubbleOutlineIcon },
  approved:  { label: 'accepted it from review', colour: '#15803d', icon: CheckCircleOutlineIcon },
  resolved:  { label: 'marked it as complete', colour: '#15803d', icon: CheckCircleOutlineIcon },
  owner_reassigned: { label: 'changed the owner', colour: '#33becc', icon: EditOutlinedIcon },
  overdue_notified: { label: 'sent an overdue reminder', colour: '#b00020', icon: SendIcon },
  task_linked: { label: 'linked a task', colour: '#33becc', icon: LinkRoundedIcon },
  task_unlinked: { label: 'unlinked a task', colour: '#6b7280', icon: LinkOffRoundedIcon },
  task_match_dismissed: { label: 'turned down a suggested task', colour: '#6b7280', icon: RemoveCircleOutlineIcon },
  imported:  { label: 'brought this remedial across from the previous system', colour: '#475569', icon: AddCircleOutlineIcon },
}

const IMPORTED_LABELS = {
  created: 'Recorded in the previous system',
  resolved: 'marked it as complete in the previous system',
}

const SYSTEM_KINDS = ['owner_reassigned', 'overdue_notified', 'task_linked', 'imported']
const actorName = item => {
  if (item.user) return userName(item.user)
  if (item.payload?.legacy) return null
  if (item.payload?.authorName) return item.payload.authorName
  if (item.payload?.byName) return item.payload.byName
  if (SYSTEM_KINDS.includes(item.kind)) return 'Building Passport'
  if (item.payload?.source === 'imported' && item.kind === 'created') return null
  if (item.payload?.source === 'imported') return 'Someone'
  return userName(item.user)
}

const formatDateTime = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

const userName = (u) => {
  if (!u) return 'Someone'
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim()
  return name || 'A teammate'
}

const initials = (u) => {
  if (!u) return '?'
  const first = (u.firstName || '').charAt(0)
  const last = (u.lastName || '').charAt(0)
  return ((first + last) || '?').toUpperCase()
}

// Map raw payload field keys (post case-conversion → camelCase) to the labels
// users actually recognise. Anything not in the map falls through to a
// best-effort spaced version of the key.
const FIELD_LABEL = {
  title:           'title',
  description:     'description',
  severity:        'severity',
  suggestedDueBy:  'suggested date',
  dueDate:         'due date',
  ownerUserId:     'owner',
}
const labelFor = (key) => {
  if (FIELD_LABEL[key]) return FIELD_LABEL[key]
  return key.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()
}
const summariseUpdate = (changed) => {
  if (!changed) return null
  const parts = Object.keys(changed)
  if (parts.length === 0) return null
  return 'Changed ' + parts.map(labelFor).join(', ')
}

const ActionActivityTimeline = ({ actionId, onCommented }) => {
  const currentUser = useSelector(getCurrentUser)
  const [activities, setActivities] = useState([])
  const [comments, setComments] = useState([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [body, setBody] = useState('')
  const [openGroups, setOpenGroups] = useState({})
  const toggleGroup = gid => setOpenGroups(prev => ({ ...prev, [gid]: !prev[gid] }))

  const refresh = async () => {
    if (!actionId) return
    setLoading(true)
    try {
      const [actRes, comRes] = await Promise.all([
        ActionsAPI.listActivities(actionId),
        ActionsAPI.listComments(actionId),
      ])
      setActivities(actRes?.data?.data || [])
      setComments(comRes?.data?.data || [])
    } catch {
      // network errors are quiet — the timeline is non-critical
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { refresh() }, [actionId])

  const submit = async () => {
    const trimmed = body.trim()
    if (!trimmed || submitting) return
    setSubmitting(true)
    try {
      const res = await ActionsAPI.createComment(actionId, trimmed)
      const created = res?.data?.data
      if (created) setComments(prev => [...prev, created])
      // Pull activities again so the "commented" entry shows up too.
      const actRes = await ActionsAPI.listActivities(actionId)
      setActivities(actRes?.data?.data || [])
      setBody('')
      onCommented?.()
    } catch {
      // swallow — could surface a toast here later
    } finally {
      setSubmitting(false)
    }
  }

  // Merge activities + comment-bodies into a single chronological stream.
  // Comment bodies aren't on the activity payload (only previews) — pair each
  // 'commented' entry with its full body via the comment id.
  const stream = useMemo(() => {
    const commentsById = new Map(comments.map(c => [c.id, c]))
    return activities.map(act => {
      const base = KIND_META[act.kind] || { label: 'updated the remedial', colour: '#475569', icon: ChatBubbleOutlineIcon }
      const importedLabel = act.payload?.legacy ? 'Earlier comments from the previous system' : IMPORTED_LABELS[act.kind]
      const meta = act.payload?.source === 'imported' && importedLabel ? { ...base, label: importedLabel } : base
      let body = null
      if (act.kind === 'commented') {
        const cid = act.payload?.commentId
        const c = cid ? commentsById.get(cid) : null
        body = c?.body || act.payload?.preview || null
      }
      return { ...act, meta, body }
    })
  }, [activities, comments])

  const renderRow = (item, asDiv = false) => {
    const Icon = item.meta.icon
    return (
      <Item key={item.id} as={asDiv ? 'div' : undefined}>
        <Avatar $colour={item.meta.colour}>
          <Icon style={{ fontSize: 14 }} />
        </Avatar>
        <ItemBody>
          <ItemHead>
            {actorName(item) && <><strong>{actorName(item)}</strong>{' '}</>}
            {item.meta.label}
            <Time>{formatDateTime(item.createdAt)}</Time>
          </ItemHead>
          {['updated', 'approved', 'owner_reassigned'].includes(item.kind) && item.payload?.changed && (
            <ItemDetail>{summariseUpdate(item.payload.changed)}</ItemDetail>
          )}
          {['task_linked', 'task_unlinked', 'task_match_dismissed'].includes(item.kind) && item.payload?.taskTitle && (
            <ItemDetail>{item.payload.taskTitle}</ItemDetail>
          )}
          {item.kind === 'dismissed' && item.payload?.reason && (
            <ItemDetail>“{item.payload.reason}”</ItemDetail>
          )}
          {item.body && (
            <CommentBody>{item.body}</CommentBody>
          )}
        </ItemBody>
      </Item>
    )
  }

  const groupedStream = groupConsecutive(
    stream,
    it => `${it.kind}::${it.user?.id || 'system'}`,
    it => it.kind !== 'commented'
  )

  return (
    <Wrapper>
      <SectionHead>Activity & comments</SectionHead>
      {loading && stream.length === 0 ? (
        <Empty>Loading activity…</Empty>
      ) : stream.length === 0 ? (
        <Empty>No activity yet. Be the first to leave a comment.</Empty>
      ) : (
        <Timeline>
          {groupedStream.map((group, gi) => {
            if (group.items.length === 1) return renderRow(group.items[0])
            const first = group.items[0]
            const Icon = first.meta.icon
            const gid = `${first.id}-${gi}`
            const open = Boolean(openGroups[gid])
            return (
              <GroupItem key={gid}>
                <GroupSummary onClick={() => toggleGroup(gid)}>
                  <Avatar $colour={first.meta.colour}>
                    <Icon style={{ fontSize: 14 }} />
                  </Avatar>
                  <GroupText>
                    <span>
                      <strong>{actorName(first)}</strong>
                      {' '}{first.meta.label}
                      <GroupTimes>{group.items.length} times</GroupTimes>
                    </span>
                    <Time>{formatDateTime(first.createdAt)}</Time>
                  </GroupText>
                  {open ? <KeyboardArrowUpIcon style={{ fontSize: 18 }} /> : <KeyboardArrowDownIcon style={{ fontSize: 18 }} />}
                </GroupSummary>
                {open && (
                  <GroupMembers>
                    {group.items.map(it => renderRow(it, true))}
                  </GroupMembers>
                )}
              </GroupItem>
            )
          })}
        </Timeline>
      )}

      <Composer>
        <ComposerAvatar title={userName(currentUser)}>{initials(currentUser)}</ComposerAvatar>
        <ComposerField
          rows={2}
          value={body}
          placeholder="Leave a comment for the team…"
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') submit()
          }}
        />
        <ComposerSubmit
          type="button"
          onClick={submit}
          disabled={!body.trim() || submitting}
          title="Post comment (⌘/Ctrl+Enter)"
        >
          <SendIcon style={{ fontSize: 16 }} />
        </ComposerSubmit>
      </Composer>
    </Wrapper>
  )
}

export default ActionActivityTimeline

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`

const SectionHead = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#64748b'};
  font-weight: 600;
`

const Empty = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#64748b'};
  padding: 12px 0;
`

const Timeline = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
`

const GroupItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const GroupSummary = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  cursor: pointer;
`

const GroupText = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 13px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const GroupTimes = styled.span`
  display: inline-block;
  margin-left: 8px;
  font-size: 11px;
  font-weight: 700;
  color: #667085;
  background: #eef0f3;
  border-radius: 999px;
  padding: 1px 8px;
`

const GroupMembers = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-left: 16px;
  padding-left: 12px;
  border-left: 2px solid #e4e7ec;
`

const Item = styled.li`
  display: flex;
  gap: 12px;
  align-items: flex-start;
`

const Avatar = styled.div`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: ${({ $colour }) => $colour ? $colour + '14' : '#f1f5f9'};
  color: ${({ $colour }) => $colour || '#475569'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ $colour }) => $colour ? $colour + '40' : '#e2e8f0'};
`

const ItemBody = styled.div`
  flex: 1;
  min-width: 0;
`

const ItemHead = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
  line-height: 1.45;
`

const Time = styled.span`
  display: inline-block;
  margin-left: 8px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#94a3b8'};
`

const ItemDetail = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#475569'};
  margin-top: 2px;
`

const CommentBody = styled.div`
  margin-top: 6px;
  background: #f8fafc;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.haiti};
  white-space: pre-wrap;
`

const Composer = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding-top: 14px;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};
`

const ComposerAvatar = styled.div`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  font-size: 12px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`

const ComposerField = styled.textarea`
  flex: 1;
  min-height: 50px;
  resize: vertical;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.haiti};
  outline: none;
  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(210, 71, 36, 0.08);
  }
`

const ComposerSubmit = styled.button`
  flex-shrink: 0;
  align-self: stretch;
  width: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s;
  &:hover:not(:disabled) { background: ${({ theme }) => theme.colors.primary}dd; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`
